/**
 * Individual Blog Post Page
 * SEO-optimized with schema markup, CTAs, and social sharing
 */

import { useState, useEffect } from 'react';
import { useRoute, Link } from 'wouter';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Clock,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Share2,
  Linkedin,
  Twitter,
  Copy,
  Check,
  Tag,
  User,
  Loader2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import {
  trackBlogPostView,
  trackBlogCTAClick,
  trackBlogTimeOnPage,
  trackBlogRelatedClick
} from '@/lib/tracking-helpers';
import { sanitizeBlogContent } from '@/lib/sanitize';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
  keywords: string[];
  category: string;
  tags: string[];
  featured_image: string;
  image_alt: string;
  author_name: string;
  read_time_minutes: number;
  publish_at: string;
  cta_type: string;
  cta_text: string;
  cta_url: string;
}

// SEO Head component (updates document head)
function SEOHead({ post }: { post: BlogPost }) {
  useEffect(() => {
    // Update title
    document.title = post.meta_title || `${post.title} | Prozpector Blog`;

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', post.meta_description || post.excerpt);

    // Update keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', post.keywords?.join(', ') || '');

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: post.title },
      { property: 'og:description', content: post.excerpt },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:image', content: post.featured_image || '/og-default.png' },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Twitter Card
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: post.title },
      { name: 'twitter:description', content: post.excerpt },
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Cleanup
    return () => {
      document.title = 'Prozpector - Sales Automation CRM';
    };
  }, [post]);

  return null;
}

// Article Schema markup
function ArticleSchema({ post }: { post: BlogPost }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Organization',
      name: post.author_name || 'Prozpector Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Prozpector',
      logo: {
        '@type': 'ImageObject',
        url: 'https://prozpector.com/logo.png',
      },
    },
    datePublished: post.publish_at,
    dateModified: post.publish_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// CTA Component - Service Promotion
function ArticleCTA({ post }: { post: BlogPost }) {
  const handleCTAClick = (ctaType: 'booking' | 'pricing') => {
    trackBlogCTAClick(post.slug, 'article', ctaType === 'booking' ? 'Book Discovery Call' : 'See What Included');
  };

  const bookingUrl = "https://cal.com/scott-dudley-ftors8/15min";

  return (
    <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 my-12">
      <div className="text-center">
        <div className="inline-block bg-white/20 text-white text-sm font-semibold px-3 py-1 rounded-full mb-4">
          Done-For-You Outbound
        </div>
        <h3 className="text-2xl font-bold mb-4">
          Ready to Fill Your Calendar?
        </h3>
        <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
          We handle the entire outbound process - from prospect research to meeting booking. You just show up and close.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              variant="secondary"
              className="text-primary"
              onClick={() => handleCTAClick('booking')}
            >
              Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => handleCTAClick('pricing')}
            >
              Get a Quote
            </Button>
          </a>
        </div>
      </div>
    </Card>
  );
}

// Related Posts Component
function RelatedPosts({ currentSlug, category }: { currentSlug: string; category: string }) {
  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    loadRelatedPosts();
  }, [currentSlug, category]);

  const loadRelatedPosts = async () => {
    const { data } = await supabase
      .from('blog_posts')
      .select('slug, title, excerpt, category, read_time_minutes, publish_at')
      .eq('status', 'published')
      .lte('publish_at', new Date().toISOString())
      .neq('slug', currentSlug)
      .limit(3)
      .order('publish_at', { ascending: false });

    if (data) {
      setPosts(data as BlogPost[]);
    }
  };

  const handleRelatedClick = (toSlug: string) => {
    trackBlogRelatedClick(currentSlug, toSlug);
  };

  if (posts.length === 0) return null;

  return (
    <section className="py-12">
      <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link key={post.slug} href={`/blog/${post.slug}`}>
            <Card
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full"
              onClick={() => handleRelatedClick(post.slug)}
            >
              <Badge className="mb-3">{post.category}</Badge>
              <h3 className="font-bold mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-sm text-gray-600 line-clamp-2 mb-4">{post.excerpt}</p>
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 mr-1" />
                {post.read_time_minutes} min read
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function BlogPostPage() {
  const [match, params] = useRoute('/blog/:slug');
  const slug = match && params ? (params as { slug: string }).slug : '';

  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  // Track blog post view and time on page
  useEffect(() => {
    if (!post) return;

    // Track page view
    trackBlogPostView(post.slug, post.title, post.category);

    // Track time on page
    const startTime = Date.now();

    const handleBeforeUnload = () => {
      const timeSeconds = Math.round((Date.now() - startTime) / 1000);
      // Only track if user spent at least 5 seconds
      if (timeSeconds >= 5) {
        trackBlogTimeOnPage(post.slug, timeSeconds);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Track time when component unmounts or user navigates away
      const timeSeconds = Math.round((Date.now() - startTime) / 1000);
      if (timeSeconds >= 5) {
        trackBlogTimeOnPage(post.slug, timeSeconds);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [post]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .lte('publish_at', new Date().toISOString())
        .single();

      if (error) throw error;
      setPost(data);
    } catch (err) {
      console.error('Error loading post:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Link copied!' });
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post?.title || '');
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
  };

  if (isLoading) {
    return (
      <MarketingLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MarketingLayout>
    );
  }

  if (!post) {
    return (
      <MarketingLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">This article doesn't exist or hasn't been published yet.</p>
          <Link href="/blog">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
            </Button>
          </Link>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <SEOHead post={post} />
      <ArticleSchema post={post} />

      {/* Article Header */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-16 md:py-24">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Back link */}
            <Link href="/blog">
              <Button variant="ghost" className="mb-8">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
              </Button>
            </Link>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Badge variant="secondary" className="text-sm">
                {post.category}
              </Badge>
              <span className="flex items-center text-gray-600 text-sm">
                <Clock className="h-4 w-4 mr-1" />
                {post.read_time_minutes} min read
              </span>
              <span className="flex items-center text-gray-600 text-sm">
                <Calendar className="h-4 w-4 mr-1" />
                {new Date(post.publish_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-gray-600 mb-8">
              {post.excerpt}
            </p>

            {/* Author & Share */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{post.author_name}</p>
                  <p className="text-sm text-gray-600">Prozpector Team</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 mr-2">Share:</span>
                <Button variant="outline" size="icon" onClick={shareOnLinkedIn}>
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={shareOnTwitter}>
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={copyLink}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Main content - rendered as HTML */}
            <article
              className="prose prose-lg max-w-none prose-headings:font-bold prose-h2:text-3xl prose-h3:text-2xl prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              [&_.cta-box]:bg-black [&_.cta-box]:text-white [&_.cta-box]:p-8 [&_.cta-box]:rounded-lg [&_.cta-box]:my-8 [&_.cta-box]:text-center
              [&_.cta-box.cta-final]:bg-gradient-to-r [&_.cta-box.cta-final]:from-gray-900 [&_.cta-box.cta-final]:to-black
              [&_.cta-box_h3]:text-white [&_.cta-box_h3]:text-2xl [&_.cta-box_h3]:font-bold [&_.cta-box_h3]:mb-4
              [&_.cta-box_p]:text-gray-200 [&_.cta-box_p]:mb-6 [&_.cta-box_p]:text-lg
              [&_.cta-badge]:inline-block [&_.cta-badge]:bg-white/20 [&_.cta-badge]:text-white [&_.cta-badge]:text-sm [&_.cta-badge]:font-semibold [&_.cta-badge]:px-3 [&_.cta-badge]:py-1 [&_.cta-badge]:rounded-full [&_.cta-badge]:mb-4
              [&_.cta-button]:inline-block [&_.cta-button]:bg-primary [&_.cta-button]:text-white [&_.cta-button]:px-6 [&_.cta-button]:py-3 [&_.cta-button]:rounded-lg [&_.cta-button]:font-semibold [&_.cta-button]:no-underline [&_.cta-button]:transition-all
              [&_.cta-button:hover]:bg-primary/90 [&_.cta-button:hover]:transform [&_.cta-button:hover]:scale-105"
              dangerouslySetInnerHTML={{ __html: sanitizeBlogContent(post.content) }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 my-8">
                <Tag className="h-4 w-4 text-gray-500" />
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <Separator className="my-12" />

            {/* Related Posts */}
            <RelatedPosts currentSlug={post.slug} category={post.category} />
          </div>
        </div>
      </section>

      {/* Bottom CTA - Beta Program */}
      <section className="py-16 bg-black text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-primary/20 text-primary-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Beta Program - 10 Spots Available
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Fill Your Calendar?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              We handle the entire outbound process - from prospect research to meeting booking. You just show up and close.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => trackBlogCTAClick(post.slug, 'bottom', 'Book Discovery Call')}
                >
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link href="/features">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => trackBlogCTAClick(post.slug, 'bottom', 'See What We Deliver')}
                >
                  See What We Deliver
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
